// Shows the current notice (FR-033; data-model §12): a polite live region (web role="status",
// Android accessibilityLiveRegion), dismissed after 6 s or by its labelled 44 px close button.
import { useEffect, useSyncExternalStore } from "react";
import { StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { makeStyles } from "@/theme/makeStyles";

import { useChromeKind } from "../chrome/useChromeKind";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { appNotices, type NoticeStore } from "./store";

export const NOTICE_MS = 6_000;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    wrap: {
      position: "absolute",
      left: t.space.lg,
      right: t.space.lg,
      bottom: t.space.lg,
      alignItems: "center",
      zIndex: 20,
    },
    notice: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.md,
      maxWidth: 480,
      paddingLeft: t.space.lg,
      backgroundColor: t.color.infoSoft,
      borderColor: t.color.border,
      borderWidth: t.borderWidth.card,
      borderRadius: t.radius.panel,
      boxShadow: t.shadow.card,
    },
    text: { flexShrink: 1, color: t.color.info },
    close: { paddingHorizontal: t.space.md },
    // Phones: an Android snackbar. Full width inside the gutters, the inverse colours, sitting
    // above the tab bar or a screen's action bar, with a text action.
    snackWrap: { left: t.space.sm, right: t.space.sm },
    snack: {
      alignSelf: "stretch",
      maxWidth: undefined,
      minHeight: 48,
      paddingLeft: t.space.lg,
      backgroundColor: t.color.accent,
      borderWidth: 0,
      borderRadius: t.radius.softPanel,
    },
    snackText: { flex: 1, color: t.color.onAccent },
    snackAction: { color: t.color.onAccent, textTransform: "lowercase" },
  }),
);

export function NoticeHost({ store = appNotices }: { store?: NoticeStore }) {
  const styles = useStyles();
  const phone = useChromeKind() === "mobile";
  const insets = useSafeAreaInsets();
  const notice = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => store.dismiss(notice.id), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice, store]);

  if (!notice) return null;
  return (
    <View
      style={[
        styles.wrap,
        // Clear of the tab bar (or a screen's action bar) and the gesture bar.
        phone && [styles.snackWrap, { bottom: insets.bottom + 80 }],
      ]}
      pointerEvents="box-none"
    >
      <View
        style={[styles.notice, phone && styles.snack]}
        role="status"
        accessibilityLiveRegion="polite"
        testID="notice"
      >
        <Text style={phone ? styles.snackText : styles.text}>
          {notice.text}
        </Text>
        <PressableTarget
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          onPress={() => store.dismiss(notice.id)}
          style={styles.close}
        >
          {phone ? (
            <Text weight="semiBold" style={styles.snackAction}>
              Dismiss
            </Text>
          ) : (
            <Text weight="semiBold" style={styles.text}>
              ×
            </Text>
          )}
        </PressableTarget>
      </View>
    </View>
  );
}
